import React, { useImperativeHandle, useRef, useState } from "react";
import Note from "./Note";
import { validateNoteText } from "../../../../helpers/taskValidation";
import { Box, Chip, Collapse, IconButton, Typography } from "@mui/material";
import NoteIcon from "@mui/icons-material/StickyNote2Outlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import useWindowDimensions from "../../../../helpers/window";

const NotesContainer = ({
  showInput = true,
  inline = false,
  notes,
  onAddNote,
  onNoteStatusChange,
  onNoteDeleted,
  noteInputValueRef,
  noteInputError,
  onNoteInputErrorChange,
  mock,
}) => {
  const [noteInput, setNoteInput] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef();
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  useImperativeHandle(
    noteInputValueRef,
    () => ({
      getNoteValue: () => {
        return noteInput;
      },
    }),
    [noteInput],
  );

  const noteCount = notes ? Object.keys(notes).length : 0;

  const commitNote = (text) => {
    const trimmed = text.trim();
    if (!trimmed) return;

    const validation = validateNoteText(trimmed);
    if (!validation.isValid) {
      if (onNoteInputErrorChange) {
        onNoteInputErrorChange(validation.error);
      }
      return;
    }

    onAddNote(trimmed);
    setNoteInput("");
    if (onNoteInputErrorChange) {
      onNoteInputErrorChange("");
    }
  };

  const onNoteInputChange = (event) => {
    const newNoteInput = event.target.value;
    setNoteInput(newNoteInput);

    // Validate note length in real-time
    if (onNoteInputErrorChange) {
      const validation = validateNoteText(newNoteInput);
      if (!validation.isValid) {
        onNoteInputErrorChange(validation.error);
      } else if (noteInputError) {
        onNoteInputErrorChange("");
      }
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      commitNote(noteInput);
    }
  };

  const onBlur = () => {
    if (noteInput.trim()) {
      commitNote(noteInput);
    }
  };

  const onAddButtonClicked = () => {
    commitNote(noteInput);
    inputRef.current?.focus();
  };

  if (!showInput) {
    // Read-only / display mode (used in task card)
    return (
      <a className="notes-container">
        <div className="notes-container__notes">
          {notes &&
            Object.values(notes).map((note) => (
              <Note
                id={note.id}
                text={note.text}
                completed={note.completed}
                onNoteStatusChange={onNoteStatusChange}
                onNoteDeleted={onNoteDeleted}
                key={note.id}
                mock={mock}
              />
            ))}
        </div>
      </a>
    );
  }

  // Inline mode — used inside a panel that already has its own card/container
  if (inline) {
    return (
      <div className="notes-group__body notes-group__body--inline">
        <div className="notes-group__input-row">
          <textarea
            ref={inputRef}
            className={`notes-group__textarea${noteInputError ? " notes-group__textarea--error" : ""}`}
            placeholder={
              isMobile ? "Type a note…" : "Type a note and press Enter…"
            }
            value={noteInput}
            onChange={onNoteInputChange}
            onKeyDown={onKeyDown}
            onBlur={onBlur}
            rows={1}
          />
          <button
            type="button"
            className={`notes-group__add-btn${noteInput.trim() ? " notes-group__add-btn--active" : ""}`}
            onClick={onAddButtonClicked}
            tabIndex={-1}
            aria-label="Add note"
          >
            <AddIcon fontSize="small" />
          </button>
        </div>
        {noteInputError && (
          <Typography variant="caption" className="notes-group__error">
            {noteInputError}
          </Typography>
        )}
        {noteCount > 0 && (
          <div className="notes-container__notes">
            {Object.values(notes).map((note) => (
              <Note
                id={note.id}
                text={note.text}
                completed={note.completed}
                onNoteStatusChange={onNoteStatusChange}
                onNoteDeleted={onNoteDeleted}
                key={note.id}
                mock={mock}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="notes-group">
      <Box
        className="notes-group__header"
        onClick={() => {
          if (!isOpen) {
            setIsOpen(true);
            setTimeout(() => inputRef.current?.focus(), 300);
          } else {
            setIsOpen(false);
          }
        }}
      >
        <Box className="notes-group__icon-box">
          <NoteIcon sx={{ color: "#fff", fontSize: "20px" }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: "#333", lineHeight: 1.2 }}
          >
            Notes
          </Typography>
          {!isOpen && noteCount > 0 ? (
            <Box className="notes-group__summary-chips">
              {Object.values(notes).map((note) => (
                <Chip
                  key={note.id}
                  icon={
                    note.completed ? (
                      <CheckCircleOutlineIcon
                        sx={{ fontSize: "13px !important" }}
                      />
                    ) : (
                      <RadioButtonUncheckedIcon
                        sx={{ fontSize: "13px !important" }}
                      />
                    )
                  }
                  label={
                    note.text.length > 30
                      ? note.text.slice(0, 30) + "…"
                      : note.text
                  }
                  size="small"
                  className={`notes-group__summary-chip${note.completed ? " notes-group__summary-chip--done" : ""}`}
                />
              ))}
            </Box>
          ) : (
            <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.5)" }}>
              {isOpen
                ? `${noteCount} note${noteCount !== 1 ? "s" : ""}`
                : "Add context or sub-tasks"}
            </Typography>
          )}
        </Box>
        <IconButton
          size="small"
          className="notes-group__expand-btn"
          tabIndex={-1}
        >
          {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={isOpen} timeout={300}>
        <div className="notes-group__body">
          <div className="notes-group__input-row">
            <textarea
              ref={inputRef}
              className={`notes-group__textarea${noteInputError ? " notes-group__textarea--error" : ""}`}
              placeholder={
                isMobile ? "Type a note…" : "Type a note and press Enter…"
              }
              value={noteInput}
              onChange={onNoteInputChange}
              onKeyDown={onKeyDown}
              onBlur={onBlur}
              rows={1}
            />
            <button
              type="button"
              className={`notes-group__add-btn${noteInput.trim() ? " notes-group__add-btn--active" : ""}`}
              onClick={onAddButtonClicked}
              tabIndex={-1}
              aria-label="Add note"
            >
              <AddIcon fontSize="small" />
            </button>
          </div>
          {noteInputError && (
            <Typography variant="caption" className="notes-group__error">
              {noteInputError}
            </Typography>
          )}

          {noteCount > 0 && (
            <div className="notes-container__notes">
              {Object.values(notes).map((note) => (
                <Note
                  id={note.id}
                  text={note.text}
                  completed={note.completed}
                  onNoteStatusChange={onNoteStatusChange}
                  onNoteDeleted={onNoteDeleted}
                  key={note.id}
                  mock={mock}
                />
              ))}
            </div>
          )}
        </div>
      </Collapse>
    </div>
  );
};

export default NotesContainer;
